import React from "react"
import { Form, useParams, useNavigate } from "react-router-dom"
import BIJSONEditor from "./JSONEditor"
import { Api } from "@/lib/api"
import {
  Alert,
  Button,
  Heading,
  Input,
  ListItem,
  Link,
  Text,
  UnorderedList,
  SimpleGrid,
  GridItem,
  VStack,
  Stack,
  AlertIcon,
  AlertTitle,
  Flex,
} from "@chakra-ui/react"
import { Datasets, CurrentOrg } from "@/lib/store"
import { unrollError } from "@/lib/util"
import {
  PRO_ACCOUNT_MAX_FIELDS,
  CONTACT_SALES_URL,
  isProAccount,
} from "@/lib/limits"

export default function SchemaCreate() {
  const [error, setError] = React.useState(null)
  const [dataset, setDataset] = React.useState({})
  const [datasets] = Datasets.useState()
  const { datasetId } = useParams()
  const [currentOrg] = CurrentOrg.useState()
  const [fieldCount, setFieldCount] = React.useState(0)
  React.useEffect(() => {
    setDataset(datasets[datasetId])
  }, [datasets])

  const api = new Api()

  // Example schema
  const initialContent = {
    type: "object",
    properties: {
      name: {
        type: "string",
      },
      age: {
        type: "integer",
        minimum: 1,
        maximum: 100,
      },
    },
    required: ["name", "age"],
  }

  // Set the initial content to a pretty indented version that can be immediately submitted.
  const [jsonContent, setJsonContent] = React.useState({
    json: undefined,
    text: JSON.stringify(initialContent, null, 2),
  })

  const onChangeJSON = React.useCallback(
    (updatedContent, previousContent, status) => {
      console.log("onChange", { updatedContent, previousContent, status })
      setJsonContent(updatedContent)
      console.log(">>> jsonContent", jsonContent)

      if (isProAccount(currentOrg) && updatedContent.text) {
        try {
          const parsed = JSON.parse(updatedContent.text)
          const count = Object.keys(parsed?.properties || {}).length
          setFieldCount(count)
        } catch (e) {
          setFieldCount(0)
        }
      }
    },
    [jsonContent, currentOrg],
  )

  const navigate = useNavigate()

  const handleSubmit = async (event) => {
    event.preventDefault()
    const { name, description } = event.target.elements
    let schema

    // Invalid JSON will not parse.
    try {
      schema = JSON.parse(jsonContent.text)
    } catch (e) {
      setError(
        "JSON schema is invalid. Please correct the errors before submitting.",
      )
      return
    }

    if (isProAccount(currentOrg)) {
      const count = Object.keys(schema?.properties || {}).length
      if (count > PRO_ACCOUNT_MAX_FIELDS) {
        setError(
          `Schema creation failed: Schema contains ${count} fields. Pro accounts support up to ${PRO_ACCOUNT_MAX_FIELDS} fields. Talk to sales if you need more. Visit ${CONTACT_SALES_URL} to contact sales.`,
        )
        return
      }
    }

    try {
      await api.schemasCreate(
        dataset.url,
        name.value,
        description.value,
        schema,
      )
      setError(null)
      await api.schemasList()
      navigate(`/datasets/${datasetId}`)
    } catch (e) {
      console.log("Failed to create schema", e)
      setError(await unrollError(e))
    }
  }

  return (
    <Flex
      flexDirection="column"
      alignItems={"left"}
      m={{
        base: "0em 1em 0em 1em",
        md: "0em 2em 0em 2em",
        lg: "0em 2em 0em 2em",
      }}
      height="100%"
      minHeight={0}
      borderRadius="1em"
      overflowY="hidden"
    >
      <Heading variant="standardMicro" mt="0em">
        Add a new Schema
      </Heading>
      <Form
        onSubmit={handleSubmit}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SimpleGrid
          columns={{ sm: 3, md: 3, lg: 3 }}
          gap={{ base: "0.25em", md: "0.5em", lg: "1em" }}
          height="1px"
          flex="1 1 0"
          minHeight={0}
        >
          <GridItem colSpan={{ sm: 3, md: 1, lg: 1 }}>
            <VStack gap={{ base: "0.25em", md: "0.5em", lg: "1em" }}>
              <VStack
                gap="0"
                alignItems={"left"}
                justifyContent="left"
                width="100%"
              >
                <Input
                  size={{ base: "sm", md: "sm", lg: "md" }}
                  variant="standardMdl"
                  name="name"
                  placeholder="Enter a name for your schema"
                  id="fld-schema-name"
                  isRequired
                />
                <Text color="black" fontSize="xs" ml="1em">
                  Human-readable name
                </Text>
              </VStack>
              <VStack
                gap="0"
                alignItems={"left"}
                justifyContent="left"
                width="100%"
              >
                <Input
                  size={{ base: "sm", md: "sm", lg: "md" }}
                  variant="standardMdl"
                  name="description"
                  placeholder="Description"
                  id="fld-schema-description"
                />
                <Text color="black" fontSize="xs" ml="1em">
                  Brief description
                </Text>
              </VStack>
            </VStack>
            {error && <Alert status="error">{error}</Alert>}
          </GridItem>
          <GridItem
            colSpan={{ sm: 3, md: 2, lg: 2 }}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            <BIJSONEditor
              content={jsonContent}
              onChange={onChangeJSON}
              readOnly={false}
              mode="text"
              mainMenuBar={false}
              id="fld-schema-content"
            />
            {isProAccount(currentOrg) &&
              fieldCount >= PRO_ACCOUNT_MAX_FIELDS && (
                <Alert status="warning" mt="0.5em">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Field limit reached ({fieldCount}/{PRO_ACCOUNT_MAX_FIELDS}).{" "}
                    <Link
                      href={CONTACT_SALES_URL}
                      isExternal
                      textDecoration="underline"
                    >
                      Talk to sales
                    </Link>{" "}
                    if you need more fields.
                  </Text>
                </Alert>
              )}
          </GridItem>
        </SimpleGrid>
        <Flex mt={{ base: "0.25em", md: "1em" }} width="100%">
          <Button
            size={{ base: "sm", md: "sm", lg: "md" }}
            ml="auto"
            variant="primary"
            type="submit"
            text="Create schema"
            float={"right"}
            isDisabled={
              isProAccount(currentOrg) && fieldCount > PRO_ACCOUNT_MAX_FIELDS
            }
          >
            Create
          </Button>
        </Flex>
        <Stack
          gap={{ base: "0.25em", md: "0.5em", lg: "1em" }}
          mt={{ base: "0.25em", md: "1em" }}
        >
          <Alert
            status="info"
            flexDirection="column"
            alignItems={"flex-start"}
            padding={{ base: "0.5em", md: "1em", lg: "1em" }}
          >
            <Stack flexDirection="row">
              <AlertIcon />
              <AlertTitle fontSize={{ base: "sm", md: "md", lg: "lg" }}>
                Blind Insight currently supports JSON Schema format
              </AlertTitle>
            </Stack>
            <UnorderedList fontSize={{ base: "sm", md: "md", lg: "lg" }}>
              <ListItem>
                Use the sample schema above as a guide. Learn more in the{" "}
                <Link
                  textDecoration="underline"
                  href="https://docs.blindinsight.io/overview/what-is-blindinsight/#schemas"
                  isExternal
                >
                  Schema documentation
                </Link>
              </ListItem>
              <ListItem>
                Schemas must be in{" "}
                <Link
                  textDecoration="underline"
                  href="https://json-schema.org/understanding-json-schema/reference"
                  isExternal
                >
                  JSON Schema
                </Link>{" "}
                format and consist of fields with <code>string</code> or{" "}
                <code>number</code> types.
              </ListItem>
              <ListItem>
                Each number field must include values for it&apos;s{" "}
                <code>minimum</code> and <code>maximum</code> bounds.
              </ListItem>
            </UnorderedList>{" "}
          </Alert>
        </Stack>
      </Form>
    </Flex>
  )
}
